import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import {
  ShopKitModel,
  ShopSiteData,
  ShopHeroConfig,
  ShopSiteSettings,
  ShopTestimonial,
  DEFAULT_HERO,
  DEFAULT_SETTINGS,
} from '../types/shop';

const CATALOG_COLLECTION = 'shop_catalog';
const SITE_CONFIG_DOC = 'settings/shop_site_config';
const TESTIMONIALS_COLLECTION = 'shop_testimonials';

// ─── Catalog ────────────────────────────────────────────────────────────────

export function subscribeToCatalog(
  callback: (kits: ShopKitModel[]) => void,
): () => void {
  const q = query(collection(db, CATALOG_COLLECTION), orderBy('sortOrder', 'asc'));
  return onSnapshot(q, (snapshot) => {
    const kits: ShopKitModel[] = [];
    snapshot.forEach((d) => {
      const data = d.data();
      kits.push({
        ...data,
        id: d.id,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
      } as ShopKitModel);
    });
    callback(kits);
  });
}

export async function saveKit(kit: Partial<ShopKitModel> & { id: string }): Promise<void> {
  const ref = doc(db, CATALOG_COLLECTION, kit.id);
  const { id, ...rest } = kit;
  await setDoc(ref, { ...rest, updatedAt: serverTimestamp() }, { merge: true });
}

export async function createKit(kit: Omit<ShopKitModel, 'createdAt' | 'updatedAt'>): Promise<string> {
  const ref = doc(db, CATALOG_COLLECTION, kit.id);
  await setDoc(ref, {
    ...kit,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return kit.id;
}

export async function deleteKit(kitId: string): Promise<void> {
  await deleteDoc(doc(db, CATALOG_COLLECTION, kitId));
}

export async function reorderKits(orderedIds: string[]): Promise<void> {
  const batch = writeBatch(db);
  orderedIds.forEach((id, i) => {
    batch.update(doc(db, CATALOG_COLLECTION, id), { sortOrder: i, updatedAt: serverTimestamp() });
  });
  await batch.commit();
}

// ─── Site Config (hero + settings) ──────────────────────────────────────────

export function subscribeToSiteConfig(
  callback: (data: ShopSiteData) => void,
): () => void {
  const ref = doc(db, SITE_CONFIG_DOC);
  return onSnapshot(ref, (snap) => {
    if (snap.exists()) {
      const d = snap.data();
      callback({
        hero: { ...DEFAULT_HERO, ...d.hero },
        settings: { ...DEFAULT_SETTINGS, ...d.settings },
        updatedAt: d.updatedAt?.toDate?.() || new Date(),
        updatedBy: d.updatedBy || '',
      });
    } else {
      callback({
        hero: { ...DEFAULT_HERO },
        settings: { ...DEFAULT_SETTINGS },
        updatedAt: new Date(),
        updatedBy: '',
      });
    }
  });
}

export async function saveHeroConfig(
  hero: ShopHeroConfig,
  userEmail: string,
): Promise<void> {
  const ref = doc(db, SITE_CONFIG_DOC);
  await setDoc(ref, { hero, updatedAt: serverTimestamp(), updatedBy: userEmail }, { merge: true });
}

export async function saveSiteSettings(
  settings: ShopSiteSettings,
  userEmail: string,
): Promise<void> {
  const ref = doc(db, SITE_CONFIG_DOC);
  await setDoc(ref, { settings, updatedAt: serverTimestamp(), updatedBy: userEmail }, { merge: true });
}

// ─── Testimonials ────────────────────────────────────────────────────────────

export function subscribeToTestimonials(
  callback: (testimonials: ShopTestimonial[]) => void,
): () => void {
  const q = query(collection(db, TESTIMONIALS_COLLECTION), orderBy('sortOrder', 'asc'));
  return onSnapshot(q, (snapshot) => {
    const items: ShopTestimonial[] = [];
    snapshot.forEach((d) => {
      const data = d.data();
      items.push({
        ...data,
        id: d.id,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
      } as ShopTestimonial);
    });
    callback(items);
  });
}

export async function saveTestimonial(
  testimonial: Partial<ShopTestimonial> & { id: string },
): Promise<void> {
  const ref = doc(db, TESTIMONIALS_COLLECTION, testimonial.id);
  const { id, ...rest } = testimonial;
  await setDoc(ref, { ...rest, updatedAt: serverTimestamp() }, { merge: true });
}

export async function createTestimonial(
  testimonial: Omit<ShopTestimonial, 'id' | 'createdAt' | 'updatedAt'>,
): Promise<string> {
  const id = `testimonial-${Date.now()}`;
  const ref = doc(db, TESTIMONIALS_COLLECTION, id);
  await setDoc(ref, {
    ...testimonial,
    id,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return id;
}

export async function deleteTestimonial(id: string): Promise<void> {
  await deleteDoc(doc(db, TESTIMONIALS_COLLECTION, id));
}

// ─── Seed catalog from manifest ─────────────────────────────────────────────

export async function seedCatalogFromManifest(
  manifestModels: Array<Record<string, unknown>>,
): Promise<number> {
  const batch = writeBatch(db);
  let count = 0;

  for (let i = 0; i < manifestModels.length; i++) {
    const m = manifestModels[i] as Record<string, unknown>;
    const id = m.id as string;
    const ref = doc(db, CATALOG_COLLECTION, id);
    const existing = await getDoc(ref);
    if (!existing.exists()) {
      batch.set(ref, {
        id,
        name: m.name || '',
        type: m.type || 'cadwork-html',
        href: m.href || '',
        description: m.description || '',
        tag: m.tag || 'Other',
        price: m.price || '',
        features: m.features || [],
        gallery: m.gallery || [],
        thumbnail: m.thumbnail || '',
        originalPrice: m.originalPrice || '',
        onSale: m.onSale || false,
        hidden: m.hidden || false,
        speciesPricing: m.speciesPricing || [],
        sortOrder: i,
        featured: false,
        status: m.type === 'coming-soon' ? 'draft' : 'active',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      count++;
    }
  }

  if (count > 0) await batch.commit();
  return count;
}

// ─── Export catalog as manifest JSON ────────────────────────────────────────

export function catalogToManifestJSON(kits: ShopKitModel[]): string {
  const models = kits
    .filter((k) => k.status === 'active' || k.status === 'draft')
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((k) => ({
      id: k.id,
      name: k.name,
      type: k.type,
      href: k.href,
      description: k.description,
      tag: k.tag,
      price: k.price,
      features: k.features,
      gallery: k.gallery,
      thumbnail: k.thumbnail,
      ...(k.originalPrice ? { originalPrice: k.originalPrice } : {}),
      ...(k.onSale ? { onSale: true } : {}),
      ...(k.hidden ? { hidden: true } : {}),
      ...(k.speciesPricing?.length ? { speciesPricing: k.speciesPricing } : {}),
    }));
  return JSON.stringify({ models }, null, 2);
}
